import React, { useCallback, useEffect, useState } from 'react';
import { FiCopy, FiGlobe } from 'react-icons/fi';
import {
  createWebchatSite,
  listWebchatSites,
  updateWebchatSite,
  type WebchatSite,
  type WebchatSiteInput,
} from '../../services/WebchatService';

export const DOMAIN_PATH_MESSAGE = 'Informe só o domínio, sem caminho';
export const DOMAIN_INVALID_MESSAGE = 'Origem inválida. Use o formato https://seusite.com';

interface Props {
  sectorId: number;
  onError: (message: string) => void;
}

const lines = (text: string): string[] => text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);

/** Valida um domínio por linha: aceita "site.com" ou "https://site.com"; caminho, consulta e âncora são recusados. */
export function validateOrigins(origins: string[]): string | null {
  for (const raw of origins) {
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
    let url: URL;
    try {
      url = new URL(withScheme);
    } catch {
      return DOMAIN_INVALID_MESSAGE;
    }
    if (!url.hostname || (url.protocol !== 'https:' && url.protocol !== 'http:') || url.username) return DOMAIN_INVALID_MESSAGE;
    if (url.pathname !== '/' || url.search || url.hash) return DOMAIN_PATH_MESSAGE;
  }
  return null;
}

const withScheme = (raw: string) => (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);

const apiMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message?.trim() || fallback;

/**
 * Cartão de Configurações do chat do site: nome, domínios permitidos (um por linha), saudação, botões de resposta rápida e
 * pausa. Mostra a chave pública (wcs_…) com botão de copiar e o link do guia de integração. Cada setor tem um site.
 */
const WebchatSiteCard: React.FC<Props> = ({ sectorId, onError }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [site, setSite] = useState<WebchatSite | null>(null);
  const [name, setName] = useState('');
  const [origins, setOrigins] = useState('');
  const [greeting, setGreeting] = useState('');
  const [replies, setReplies] = useState('');
  const [paused, setPaused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fill = useCallback((s: WebchatSite | null) => {
    setSite(s);
    setName(s?.name ?? '');
    setOrigins((s?.allowedOrigins ?? []).join('\n'));
    setGreeting(s?.greeting ?? '');
    setReplies((s?.quickReplies ?? []).join('\n'));
    setPaused(s?.status === 'paused');
  }, []);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    setFailed(false);
    listWebchatSites(sectorId)
      .then((sites) => {
        if (!alive) return;
        fill(sites[0] ?? null);
        setLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setFailed(true);
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [sectorId, fill]);

  const save = async () => {
    setError(null);
    const list = lines(origins);
    const invalid = validateOrigins(list);
    if (invalid) {
      setError(invalid);
      return;
    }
    if (!name.trim()) {
      setError('Informe o nome do site.');
      return;
    }
    const input: WebchatSiteInput = {
      name: name.trim(),
      allowedOrigins: list.map(withScheme),
      greeting: greeting.trim() ? greeting.trim() : null,
      quickReplies: lines(replies),
      status: paused ? 'paused' : 'active',
    };
    setSaving(true);
    try {
      const saved = site ? await updateWebchatSite(sectorId, site.id, input) : await createWebchatSite(sectorId, input);
      fill(saved);
    } catch (e) {
      const message = apiMessage(e, 'Não foi possível salvar o site agora.');
      setError(message);
      onError(message);
    } finally {
      setSaving(false);
    }
  };

  const copyKey = async () => {
    if (!site) return;
    try {
      await navigator.clipboard.writeText(site.publicKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onError('Não foi possível copiar. Selecione a chave e copie manualmente.');
    }
  };

  const statusColor = site && !paused && site.status === 'active' ? 'var(--success-color)' : 'var(--text-tertiary)';
  const statusLabel = !loaded ? 'Verificando…' : failed ? 'Indisponível' : !site ? 'Não configurado' : site.status === 'paused' ? 'Pausado' : 'Ativo';

  return (
    <div className="sector-channel webchat-channel">
      <div className="sector-channel-head">
        <h4><FiGlobe size={16} /> Chat do site</h4>
        <div className="whatsapp-status" style={{ color: statusColor }}>
          <span className="status-dot" style={{ backgroundColor: statusColor }} />
          <span>{statusLabel}</span>
        </div>
      </div>
      {failed ? <p className="sector-description">Não foi possível consultar o chat do site agora.</p> : null}
      {loaded && !failed ? (
        <div className="webchat-form">
          <p className="sector-description">
            {site
              ? 'A IA atende os visitantes do seu site e a conversa cai em Conversas, no canal Site.'
              : 'Cadastre o site para a IA atender os visitantes e a conversa cair em Conversas, no canal Site.'}
          </p>
          {site ? (
            <div className="webchat-key-row">
              <span className="webchat-key-label">Chave do site</span>
              <code className="webchat-key">{site.publicKey}</code>
              <button type="button" className="whatsapp-connect-button is-quiet" onClick={() => void copyKey()}>
                <FiCopy size={13} aria-hidden="true" /> {copied ? 'Copiado' : 'Copiar chave'}
              </button>
            </div>
          ) : null}
          <label className="webchat-field">
            <span>Nome do site</span>
            <input type="text" value={name} maxLength={150} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="webchat-field">
            <span>Domínios permitidos (um por linha)</span>
            <textarea rows={3} placeholder="https://seusite.com" value={origins} onChange={(e) => setOrigins(e.target.value)} />
          </label>
          <label className="webchat-field">
            <span>Saudação</span>
            <textarea rows={2} value={greeting} onChange={(e) => setGreeting(e.target.value)} />
          </label>
          <label className="webchat-field">
            <span>Botões de resposta rápida (um por linha)</span>
            <textarea rows={3} placeholder="Book an Appointment" value={replies} onChange={(e) => setReplies(e.target.value)} />
          </label>
          {site ? (
            <label className="webchat-check">
              <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} aria-label="Pausar o chat do site" />
              <span>Pausar o chat do site (o botão some das páginas)</span>
            </label>
          ) : null}
          {error ? <div className="webchat-error" role="alert">{error}</div> : null}
          <div className="whatsapp-buttons-container">
            <button type="button" className="whatsapp-connect-button" disabled={saving} onClick={() => void save()}>
              {saving ? 'Salvando…' : site ? 'Salvar' : 'Criar site'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WebchatSiteCard;
